import * as React from "react";
import { Component } from "react";
import * as cornerstone from "cornerstone-core";
import { AuthManager } from "../../component/auth/AuthContext";
import CancelableAxiosInstance from "../../api/CancelableAxiosInstance";
import CornerstoneUtil from "../../util/CornerstoneUtil";

CornerstoneUtil.setupWADOImageLoader();

interface SimpleViewportProps {
  imagePath: string;
  imageWidth: number;
  imageHeight: number;
  /**
   * image imageOffset to apply in the viewport
   */
  imageOffset: number;
  /**
   * image imageLength to display in the viewport
   */
  imageLength: number;
  authManager: AuthManager;
}

interface SimpleViewportState {
  loading: boolean;
}

export default class SimpleViewport extends Component<SimpleViewportProps, SimpleViewportState> {
  private _domElement?: HTMLElement;
  private readonly client: CancelableAxiosInstance;

  private readonly imageLoaderScheme: string;

  private scale = 1;
  private canvasWidth: number;

  private onImageRenderListener = (e: any) => this.onImageRendered(e);

  constructor(props: SimpleViewportProps) {
    super(props);

    this.client = this.props.authManager.cancelableClient();
    this.imageLoaderScheme = CornerstoneUtil.setupLoader(this.client);

    this.state = {
      loading: true,
    };
  }

  set element(elt: any) {
    if (!elt) return;

    if (!this._domElement) {
      this._domElement = elt;

      this._domElement.addEventListener("cornerstoneimagerendered", this.onImageRenderListener);
      cornerstone.enable(this._domElement);

      this.loadImage(this.props.imagePath);
    }
  }

  componentDidUpdate() {
    cornerstone.resize(this._domElement);
  }

  loadImage(path: string) {
    this.setState({ loading: true }, () => {
      try {
        CornerstoneUtil.loadAndCacheImage(this.imageLoaderScheme, path).then((image) => {
          cornerstone.displayImage(this._domElement, image);
          // Reset cornerstone data
          cornerstone.reset(this._domElement);

          // Adapt canvas width and resize image according to scale
          this.canvasWidth = Math.min(window.innerWidth, this.props.imageWidth);

          this.scale = this.canvasWidth / this.props.imageWidth;

          // translate image according to offset
          const viewport = cornerstone.getViewport(this._domElement);
          viewport.translation = {
            x: -(this.scale * this.props.imageOffset),
            y: 0,
          };
          cornerstone.setViewport(this._domElement, viewport);
        });
      } catch (e) {
        console.error("Can't load image " + path, e);
      }
    });
  }

  onImageRendered(e: any) {
    // draw white rectangle to hide image after part
    const canvasContext = e.detail.canvasContext;

    const x = this.scale * (this.props.imageOffset + this.props.imageLength);
    const y = 0;
    const whiteRectWidth = this.scale * (this.props.imageWidth - this.props.imageLength);
    const whiteRectHeight = this.scale * this.props.imageHeight;
    canvasContext.fillStyle = "white";
    canvasContext.fillRect(x, y, whiteRectWidth, whiteRectHeight);
    canvasContext.restore();

    this.setState({ loading: false });
  }

  componentWillUnmount() {
    if (this._domElement && this.onImageRendered) {
      this._domElement.removeEventListener("cornerstoneimagerendered", this.onImageRenderListener);
    }
    CornerstoneUtil.unregisterLoader(this.imageLoaderScheme);
    this.client.cancelRequests();
  }

  render() {
    return (
      <>
        {this.state.loading && (
          <div>
            <i className="fa fa-spin fa-spinner fa-2x" /> Chargement en cours
          </div>
        )}
        <div
          ref={(elt) => (this.element = elt)}
          style={{
            width: this.canvasWidth,
            height: this.scale * this.props.imageHeight,
            visibility: this.state.loading ? "hidden" : "visible",
          }}
        />
      </>
    );
  }
}
